import { useEffect } from 'react'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import Alert from '@mui/material/Alert'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useListTenantsQuery } from '../api/authoring'
import { statusOf } from '../api/baseApi'
import { currentClaims, isOperator } from '../auth/session'
import { operator } from '../model/routes'
import { config } from '../config'
import { EmptyState, Loading, QueryError } from '../ui/QueryState'

/**
 * The boot call.
 *
 * `GET /v1/authoring/tenants` comes before anything else, because
 * tenancy is a membership rather than a claim and the token carries
 * identity rather than scope. Three answers are all ordinary and the
 * client must handle each: **none**, which is what a freshly provisioned
 * account looks like; **one**, the common case; and **several**, which is
 * an agency operating client sites and is not exotic.
 *
 * **The manifest is deliberately not fetched here any more.** It used to
 * be, on the grounds that it was a build-time constant worth warming the
 * cache with. It is per tenant now — a tenant may define component types
 * of its own — so there is nothing to fetch until this screen has done
 * its one job, and the shell reads it for whichever tenant the route
 * lands on.
 */
export function Boot() {
  const navigate = useNavigate()
  const tenants = useListTenantsQuery()

  const held = tenants.data?.tenants ?? []

  // `custom:tenant` survives as the tenant the client *opens on* — a
  // default, no longer an authorisation input. It is honoured only when
  // the caller actually holds it, because the membership set is what
  // decides and the claim is a one-hour-old opinion.
  const claims = currentClaims()
  const opensOn = claims?.['custom:tenant']
  const preferred = held.find((membership) => membership.tenant === opensOn) ?? held[0]

  // An operator with no tenant of their own starts in the operator area.
  // So does one outside the `admin` group, whom the authoring surface
  // refuses with a 403 — being an operator is standing on the platform,
  // not in any tenant.
  const operating = isOperator()
  const refused = statusOf(tenants.error) === 403
  const toOperator = operating && ((tenants.isSuccess && held.length === 0) || refused)

  useEffect(() => {
    if (toOperator) {
      navigate(operator.tenants(), { replace: true })
    } else if (held.length === 1 && preferred) {
      navigate(`/${encodeURIComponent(preferred.tenant)}`, { replace: true })
    }
  }, [toOperator, held.length, preferred, navigate])

  if (tenants.isLoading || toOperator) return <Loading label="Signing you in…" />
  if (tenants.error) return <QueryError error={tenants.error} title="Could not open your account" />

  if (held.length === 0) {
    return (
      <Stack spacing={3}>
        <EmptyState title="No site to open yet">
          <Typography color="text.secondary" sx={{ maxWidth: 520 }}>
            Your sign-in worked, but this account has not been given access to a site yet. Once access is granted, it
            applies the next time you open this page.
          </Typography>
        </EmptyState>

        {config.authMode === 'cognito' && (
          <Alert severity="info">
            Ask whoever set up your site to give <code>{claims?.email ?? claims?.sub}</code> access to it.
          </Alert>
        )}
      </Stack>
    )
  }

  if (held.length === 1) return <Loading label="Opening your site…" />

  return (
    <Stack spacing={3}>
      <Stack spacing={0.5}>
        <Typography variant="h1">Choose a tenant</Typography>
        <Typography color="text.secondary">
          You have access to more than one. Choose the one to work on.
        </Typography>
      </Stack>
      <Paper>
        <List disablePadding>
          {held.map((membership) => (
            <ListItemButton
              key={membership.tenant}
              component={RouterLink}
              to={`/${encodeURIComponent(membership.tenant)}`}
            >
              <ListItemText primary={membership.tenant} secondary={membership.role} />
            </ListItemButton>
          ))}
        </List>
      </Paper>
    </Stack>
  )
}
